/**
 * Simple implementation of a feature flag system that can be extended to featch values from backend
 * in the future.
 */

import { LS_SUBFLOWS_DEBUG_MODE_KEY } from '@prophecy/ui-v3/Graph/debugConstants';
import { StableChildren } from '@prophecy/utils/react/StableChildren';
import { noop } from 'lodash-es';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';

import {
  ALLOW_FABRIC_ACCESS_CLUSTER_ADMIN,
  COPILOT_AUDIO_PROMPT_ENABLED,
  WEBSOCKET_COMPRESSION_KEY,
  WORKFLOW_DEBUG_MODE_KEY,
  ALLOW_DATA_PROFILE_ON_INTERIMS,
  DISABLE_ZSTD_COMPRESSION,
  ENABLE_OAUTH_FABRIC_TESTING,
  ENABLE_SHARE_AND_REPLAYS_ENABLED,
  DISABLE_AUTO_SWITCH_IN_CHAT_MODE,
  ENABLE_COPY_PASTE_IN_DIFFERENT_PROVIDERS,
  ENABLE_DATA_HARMONIZATION,
  ENABLE_STRUCTURED_FINANCE,
  ENABLE_SEMANTIC_LAYER,
  ENABLE_METRICS_CONSOLE_LOGGING_KEY,
  ENABLE_SHARED_INTERIM_TABLE,
  ENABLE_SHARED_CHART_CARD
} from '../../utils/localstorage-keys';

export type FeatureValue = {
  enabled: boolean;
  localStorageKey: string;
  label: string;
  requiresPageRefresh?: boolean;
};

const INITIAL_FLAGS = {
  disableWebsocketConnection: {
    enabled: false,
    localStorageKey: WEBSOCKET_COMPRESSION_KEY,
    label: 'Disable Websocket Compression',
    requiresPageRefresh: true
  },
  enableCopilotAudioPrompt: {
    enabled: false,
    label: 'Copilot Audio Prompt',
    localStorageKey: COPILOT_AUDIO_PROMPT_ENABLED,
    requiresPageRefresh: true
  },
  enableDebugMode: {
    enabled: false,
    label: 'Debug Mode',
    localStorageKey: WORKFLOW_DEBUG_MODE_KEY
  },
  disableAutoSwitchInChatMode: {
    enabled: false,
    label: 'Disable Auto Switch in Chat Mode',
    localStorageKey: DISABLE_AUTO_SWITCH_IN_CHAT_MODE
  },
  fabricAccessClusterAdmin: {
    enabled: false,
    label: 'Allow Fabric Access Cluster Admin',
    localStorageKey: ALLOW_FABRIC_ACCESS_CLUSTER_ADMIN
  },
  interimDataProfile: {
    enabled: false,
    label: 'Allow Data Profile on interims',
    localStorageKey: ALLOW_DATA_PROFILE_ON_INTERIMS
  },
  disableZstdCompression: {
    enabled: false,
    label: 'Disable Zstd Compression',
    localStorageKey: DISABLE_ZSTD_COMPRESSION
  },
  oAuthFabricTesting: {
    enabled: false,
    label: 'OAuth Fabric Testing',
    localStorageKey: ENABLE_OAUTH_FABRIC_TESTING
  },
  shareAndReplays: {
    enabled: false,
    label: 'Share and Replays',
    localStorageKey: ENABLE_SHARE_AND_REPLAYS_ENABLED
  },
  enableCopyPasteInDifferentProviders: {
    enabled: false,
    label: 'Allow Copy Paste in Different Providers',
    localStorageKey: ENABLE_COPY_PASTE_IN_DIFFERENT_PROVIDERS
  },
  enableDataHarmonization: {
    enabled: false,
    label: 'Enable Data Harmonization',
    localStorageKey: ENABLE_DATA_HARMONIZATION
  },
  subflowsDebugMode: {
    enabled: false,
    label: 'Subflows Debug Mode',
    localStorageKey: LS_SUBFLOWS_DEBUG_MODE_KEY,
    requiresPageRefresh: true
  },
  enableStructuredFinance: {
    enabled: false,
    label: 'Structured Finance AI (replaces Harmonization in nav)',
    localStorageKey: ENABLE_STRUCTURED_FINANCE
  },
  enableSemanticLayer: {
    enabled: false,
    label: 'Semantic Layer — edit & delete metrics',
    localStorageKey: ENABLE_SEMANTIC_LAYER
  },
  enableMetricsConsoleLogging: {
    enabled: false,
    label: 'Allow Metrics Logging to Console',
    localStorageKey: ENABLE_METRICS_CONSOLE_LOGGING_KEY
  },
  /**
   * TEMPORARY — delete once the shared table is the only path.
   *
   * Renders the interim table with `@prophecy/shared-ui-ui/DataTable`, the same component the Prophecy
   * MCP table widget uses, instead of interims' own table. Off by default, so this changes nothing
   * on deploy.
   *
   * To turn it on: this row in the feature-flag dev tools, or in the console
   *   localStorage.setItem('config.sharedInterimTable.enabled', '1')
   * then refresh. `'0'` (or removing the key) puts it back.
   *
   * Verified by hand across the interim dialog, the table gem's preview tab and the dataset
   * preview: sort, filter, their persistence onto the gem, the column picker, the data profile and
   * the expanded profile all behave as interims' own table does. Known gap: Load More does not
   * retire itself once every row is fetched (cosmetic; needs a fix in ui-v3's `Table` — see
   * `InterimTableShared`).
   *
   * When this comes out, `InterimTable`'s `InterimTableOwn` path goes with it, leaving one table.
   */
  enableSharedInterimTable: {
    enabled: false,
    label: 'Interim table: use the shared DataTable (WIP)',
    localStorageKey: ENABLE_SHARED_INTERIM_TABLE,
    requiresPageRefresh: true
  },
  /**
   * TEMPORARY — delete once the shared card is the only path.
   *
   * Renders a chart widget's chrome with `@prophecy/shared-ui-ui/Charts/ChartCardShell`, the same
   * component the Prophecy MCP analysis widget uses, instead of `ChartPreviewContent`'s own JSX.
   * Off by default, so this changes nothing on deploy.
   *
   * The chart itself, its data layer and the audit panel already come from `@prophecy/shared-ui`
   * on both paths — this flag only swaps the chrome around them, which is the last piece that
   * existed twice.
   *
   * To turn it on: this row in the feature-flag dev tools, or in the console
   *   localStorage.setItem('config.sharedChartCard.enabled', '1')
   * then refresh. `'0'` (or removing the key) puts it back.
   *
   * The switch sits inside `ChartPreviewContent`, whose only consumer is `ChartsPreview` — the
   * `Preview` of every chart widget type. `WidgetNode` renders that same component in **edit mode
   * and live mode** (it differs only by a `PreviewWrapper`), so one flip covers both.
   *
   * It does **not** reach the other two places a chart is drawn: the PDF export
   * (`renderChartOffscreen`) and the interim dialog's Charts tab (`interims/Charts.tsx`) render
   * `ChartView` directly. They never had this chrome, and they already share the renderer, so
   * there is nothing for the flag to swap there and nothing to re-test on it.
   *
   * Walk these with it on, because the shell's host-owned half is exactly what the MCP gallery
   * never exercises: the title row, Load More, the chart-image download, the filter panel and its
   * Apply, the invalid-config overlay, the loading spinner, Inspect from the header and from a
   * clicked datum, and the panel's dismissal on click-outside and on scroll.
   *
   * The two paths render the same DOM by design, so with this on there is nothing to *see*. To
   * confirm which one drew a chart, look for `ChartCardShell` in React DevTools.
   *
   * Known difference, cosmetic: with no header Inspect and outside live mode the shell omits the
   * empty actions row that the old path always rendered.
   *
   * When this comes out, `ChartPreviewContent`'s own JSX goes with it, leaving one card.
   */
  enableSharedChartCard: {
    enabled: false,
    label: 'Chart widget: use the shared ChartCardShell (WIP)',
    localStorageKey: ENABLE_SHARED_CHART_CARD,
    requiresPageRefresh: true
  }
} as const;

type FeatureFlags = { [key in Feature]?: FeatureValue };

export type Feature = keyof typeof INITIAL_FLAGS;

const LS_KEY_MAPPING: Record<string, Feature> = Object.keys(INITIAL_FLAGS).reduce(
  (acc, flagKey) => {
    const val = INITIAL_FLAGS[flagKey as Feature];
    if (val && val.localStorageKey) {
      acc[val.localStorageKey] = flagKey as Feature;
    }
    return acc;
  },
  {} as Record<string, Feature>
);
const FEATURE_LS_KEY_MAPPING: Record<Feature, string> = Object.keys(INITIAL_FLAGS).reduce(
  (acc, flagKey) => {
    const val = INITIAL_FLAGS[flagKey as Feature];
    if (val) {
      acc[flagKey as Feature] = val.localStorageKey;
    }
    return acc;
  },
  {} as Record<Feature, string>
);

const FeatureFlagsContext = createContext<{
  features: FeatureFlags;
  toggleFeatureValue: (feature: Feature) => void;
  setFeatureValue: (feature: Feature, newVal: boolean) => void;
}>({
  features: INITIAL_FLAGS,
  toggleFeatureValue: noop,
  setFeatureValue: noop
});

export function FeatureFlagsProvider({ children }: PropsWithChildren<{}>) {
  const [flags, setFlags] = useState<FeatureFlags>(() =>
    Object.keys(INITIAL_FLAGS).reduce((acc, featureKey) => {
      const flag = featureKey as Feature;
      const featureValue = INITIAL_FLAGS[flag];
      if (!featureValue) {
        return acc;
      }
      const lsValue = localStorage.getItem(featureValue.localStorageKey) ?? '0';
      acc[featureKey as Feature] = {
        ...featureValue,
        enabled: lsValue === 'true' || lsValue === '1' ? true : featureValue.enabled
      };
      return acc;
    }, {} as FeatureFlags)
  );

  useEffect(() => {
    const handleStorageEvent = (ev: StorageEvent) => {
      const key = ev.key;
      if (!key || !(key in LS_KEY_MAPPING)) {
        return;
      }
      const newVal = ev.newValue;
      setFlags((currentFlags) => {
        const featureFlag = LS_KEY_MAPPING[key];
        const data = currentFlags[featureFlag] as FeatureValue;
        return {
          ...currentFlags,
          [featureFlag]: {
            ...data,
            enabled: newVal === 'true' || newVal === '1'
          }
        };
      });
    };
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, []);
  const setFeatureValue = (feature: Feature, value: boolean) => {
    setFlags((currentFlags) => ({
      ...currentFlags,
      [feature]: {
        ...currentFlags[feature],
        enabled: value
      }
    }));
    localStorage.setItem(FEATURE_LS_KEY_MAPPING[feature], value ? 'true' : 'false');
    if ('requiresPageRefresh' in INITIAL_FLAGS[feature]) {
      window.location.reload();
    }
  };
  const toggleFeatureValue = (feature: Feature) => {
    const currentValue = flags[feature]?.enabled;
    setFeatureValue(feature, !currentValue);
  };
  const context = { features: flags, toggleFeatureValue, setFeatureValue };

  return (
    <FeatureFlagsContext.Provider value={context}>
      <StableChildren>{children}</StableChildren>
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}

/**
 * Pass the feature flag key to know if a feature is enabled or not
 */
export function useIsFeatureEnabled(flag: Feature) {
  const {
    features: { [flag]: featureValue }
  } = useFeatureFlags();
  return featureValue?.enabled === true;
}
